import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
    MenuPopper,
    Menu,
    MenuItem,
    IconCog,
} from '@zillow/constellation';

import ModalActions from 'app/shared/actions/ModalActions';
import ModalConstants from 'app/shared/constants/ModalConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import userUtils from 'app/shared/utils/userUtils';
import { UserSelectors } from 'app/shared/selectors';
import ModalFooter from 'app/shared/components/Modal/ModalFooter';
import PropertiesActions from 'app/shared/actions/PropertiesActions';
import Constants from 'app/shared/constants';
import PropertyConstants from 'app/shared/constants/PropertyConstants';
import PostingActions from 'app/shared/actions/PostingActions';

import { StyledRedMenuItem } from './StyledComponents';

const { property: { unArchive, archive } } = PostingActions;
const {
    GA_EVENTS: {
        UNARCHIVE_LISTING,
        ARCHIVE_LISTING,
    },
} = AnalyticsConstants;
const { PROPERTY_TYPES_BY_CODE, PROPERTY_CODES_BY_TYPE } = PropertyConstants;

const EditPropertyTypeMenu = ({ currentSection }) => {
    const dispatch = useDispatch();
    const {
        isArchived,
        listingAlias,
        propertyType: { code },
    } = useSelector(state => state.properties.propertyOverview);
    const { roles } = useSelector(state => UserSelectors.makeUser(state));

    const [hasUpdatePropertyTypeError, setHasUpdatePropertyTypeError] = useState(false);
    const [hasArchivingError, setHasArchivingError] = useState(false);
    const [correctPropertyType, setCorrectPropertyType] = useState(PROPERTY_TYPES_BY_CODE[code]);
    const isAdmin = userUtils.isPostingPathAdmin(roles);

    const handleEditPropertySubmit = () => {
        const newPropertyTypeCode = PROPERTY_CODES_BY_TYPE[correctPropertyType];
        dispatch(PropertiesActions.updatePropertyType({
            propertyId: listingAlias,
            propertyTypeCode: newPropertyTypeCode,
        }))
            .then(() => {
                dispatch({
                    type: Constants.UPDATE_PROPERTY_OVERVIEW,
                    payload: {
                        propertyType: {
                            code: newPropertyTypeCode,
                            display: correctPropertyType,
                        },
                    },
                });
                dispatch(PropertiesActions.updateCurrentProperty({ propertyType: correctPropertyType }));
                dispatch(ModalActions.hideModal());
            })
            .catch(() => setHasUpdatePropertyTypeError(true));
    };

    const handleArchiveOrUnArchive = async () => {
        try {
            const { success } = await dispatch(
                (isArchived) ? unArchive(listingAlias) : archive(listingAlias)
            );

            if (success) {
                const gaEvent = isArchived ? UNARCHIVE_LISTING : ARCHIVE_LISTING;
                const label = listingAlias && { label: listingAlias };
                dispatch({
                    type: Constants.UPDATE_PROPERTY_OVERVIEW,
                    payload: { isArchived: !isArchived },
                });
                dispatch(PropertiesActions.updateCurrentProperty({ archived: !isArchived, isArchived: !isArchived }));
                dispatch(analyticsUtils.track.GAEventV2({ ...gaEvent, ...label }));
                dispatch(ModalActions.hideModal());
            }
        } catch (error) {
            setHasArchivingError(true);
        }
    };

    useEffect(() => {
        dispatch(ModalActions.updateModal({
            correctPropertyType,
            footer: () => (
                <ModalFooter
                    onSubmit={handleEditPropertySubmit}
                    submitText="Save"
                    onCancel={() => dispatch(ModalActions.hideModal())}
                    hasError={hasUpdatePropertyTypeError}
                    isSubmitDisabled={correctPropertyType === PROPERTY_TYPES_BY_CODE[code]}
                />
            ),
        }));
    }, [correctPropertyType]);

    useEffect(() => {
        dispatch(ModalActions.updateModal({
            footer: () => (
                <ModalFooter
                    submitText={isArchived ? 'Unarchive' : 'Archive'}
                    onSubmit={handleArchiveOrUnArchive}
                    onCancel={() => {
                        setHasArchivingError(false);
                        dispatch(ModalActions.hideModal());
                    }}
                    hasError={hasArchivingError}
                />
            ),
        }));
    }, [hasArchivingError]);

    const handleArchiveClick = () => dispatch(ModalActions.showModal({
        modalType: ModalConstants.ARCHIVE_PROPERTY_MODAL,
        header: `${isArchived ? 'Unarchive' : 'Archive'} property`,
        onClose: () => setHasArchivingError(false),
        footer: () => (
            <ModalFooter
                submitText={isArchived ? 'Unarchive' : 'Archive'}
                onSubmit={handleArchiveOrUnArchive}
                onCancel={() => {
                    setHasArchivingError(false);
                    dispatch(ModalActions.hideModal());
                }}
                hasError={hasArchivingError}
            />
        ),
    }));

    const handleAboutAddressEditsClick = () => dispatch(ModalActions.showModal({
        modalType: ModalConstants.ABOUT_ADDRESS_EDITS_MODAL,
        header: 'About address edits',
        footer: () => (
            <ModalFooter
                submitText="OK"
                onSubmit={() => dispatch(ModalActions.hideModal())}
                onCancel={() => dispatch(ModalActions.hideModal())}
            />
        ),
    }));

    const handleEditPropertyTypeClick = () => dispatch(ModalActions.showModal({
        modalType: ModalConstants.EDIT_PROPERTY_TYPE_MODAL,
        header: 'Edit property type',
        setCorrectPropertyType,
        setHasUpdatePropertyTypeError,
        correctPropertyType,
        footer: () => (
            <ModalFooter
                submitText="Save"
                onCancel={() => dispatch(ModalActions.hideModal())}
                isSubmitDisabled={correctPropertyType === PROPERTY_TYPES_BY_CODE[code]}
            />
        ),
    }));

    const handleAdminToolsClick = () => dispatch(ModalActions.updateDisplayAddPropertyModal(
        { isOpen: true, behavior: 'Edit' }
    ));

    const handleOnOpen = () => {
        const gaCategory = currentSection === 'find-tenants' ? 'FindTenants' : 'ManageTenants';
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.EDIT_PROPERTY_MENU, { category: gaCategory }
        ));
    };

    return (
        <MenuPopper
            onOpen={handleOnOpen}
            triggered={(
                <Menu>
                    <MenuItem onClick={handleArchiveClick}>
                        {`${isArchived ? 'Unarchive' : 'Archive'} property`}
                    </MenuItem>
                    {!isArchived && (
                        <>
                            <MenuItem onClick={handleAboutAddressEditsClick}>
                                About address edits
                            </MenuItem>
                            <MenuItem onClick={handleEditPropertyTypeClick}>
                                Edit property type
                            </MenuItem>
                        </>
                    )}
                    {isAdmin && (
                        <StyledRedMenuItem
                            onClick={handleAdminToolsClick}
                            icon={<IconCog />}
                        >
                            Admin tools
                        </StyledRedMenuItem>
                    )}
                </Menu>
            )}
        />
    );
};

EditPropertyTypeMenu.propTypes = {
    currentSection: PropTypes.string.isRequired,
};

export default EditPropertyTypeMenu;
